/**
 * AudioWaveform Component
 * 
 * A simple two-dimensional waveform visualization for audio playback.
 * Shows amplitude over time with smooth animation.
 */

import { useRef, useEffect, useState, useCallback } from 'react';

interface AudioWaveformProps {
  audioUrl: string;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onSeek?: (time: number) => void;
  highlightTimestamp?: number; // Timestamp in seconds to highlight
  className?: string;
}

export default function AudioWaveform({
  audioUrl,
  isPlaying,
  currentTime,
  duration,
  onSeek,
  highlightTimestamp,
  className = '',
}: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const animationRef = useRef<number | null>(null);

  // Generate waveform data from audio
  useEffect(() => {
    const generateWaveform = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        const audioContext = new AudioContextClass();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        // Get audio data
        const channelData = audioBuffer.getChannelData(0);
        const samples = 200; // Number of bars in waveform
        const blockSize = Math.floor(channelData.length / samples);
        const filteredData: number[] = [];
        
        for (let i = 0; i < samples; i++) {
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(channelData[i * blockSize + j]);
          }
          filteredData.push(sum / blockSize);
        }
        
        // Normalize
        const maxVal = Math.max(...filteredData);
        const normalized = filteredData.map(val => val / maxVal);
        
        setWaveformData(normalized);
        audioContext.close();
      } catch (error) {
        console.error('Error generating waveform:', error);
        // Generate fake waveform data as fallback
        const fakeData = Array.from({ length: 200 }, () => Math.random() * 0.5 + 0.2);
        setWaveformData(fakeData);
      }
      setIsLoading(false);
    };

    if (audioUrl) {
      generateWaveform();
    }
  }, [audioUrl]);

  // Draw waveform
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || waveformData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const barWidth = width / waveformData.length;
    const progress = duration > 0 ? currentTime / duration : 0;
    const progressIndex = Math.floor(progress * waveformData.length);
    const highlightIndex = highlightTimestamp && duration > 0 
      ? Math.floor((highlightTimestamp / duration) * waveformData.length)
      : -1;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw bars
    waveformData.forEach((value, index) => {
      const x = index * barWidth;
      const barHeight = value * (height * 0.8);
      const y = (height - barHeight) / 2;

      // Color based on progress and highlight
      if (index === highlightIndex) {
        // Highlight timestamp marker
        ctx.fillStyle = '#fbbf24'; // Yellow for highlight
      } else if (index < progressIndex) {
        // Played portion
        ctx.fillStyle = '#22c55e'; // Green
      } else {
        // Unplayed portion
        ctx.fillStyle = '#374151'; // Gray
      }

      // Draw bar with rounded corners
      const radius = Math.min(barWidth / 2, 2);
      ctx.beginPath();
      ctx.roundRect(x + 1, y, barWidth - 2, barHeight, radius);
      ctx.fill();
    });

    // Draw highlight marker line if exists
    if (highlightIndex >= 0 && highlightIndex < waveformData.length) {
      const markerX = highlightIndex * barWidth + barWidth / 2;
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 2]);
      ctx.beginPath();
      ctx.moveTo(markerX, 0);
      ctx.lineTo(markerX, height);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw playhead
    const playheadX = progress * width;
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(playheadX, 0);
    ctx.lineTo(playheadX, height);
    ctx.stroke();
  }, [waveformData, currentTime, duration, highlightTimestamp]);

  // Animation loop when playing
  useEffect(() => {
    const animate = () => {
      drawWaveform();
      if (isPlaying) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    if (isPlaying) {
      animate();
    } else {
      drawWaveform();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, drawWaveform]);

  // Handle click to seek
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onSeek || !containerRef.current || duration === 0) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const progress = x / rect.width;
    const seekTime = progress * duration;
    onSeek(Math.max(0, Math.min(seekTime, duration)));
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-16 bg-muted/30 rounded-lg ${className}`}>
        <div className="flex gap-1">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="w-1 bg-muted-foreground/30 rounded animate-pulse"
              style={{
                height: `${Math.random() * 24 + 8}px`,
                animationDelay: `${i * 50}ms`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative h-16 ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-pointer rounded-lg"
        onClick={handleClick}
      />
      {highlightTimestamp && (
        <div 
          className="absolute top-0 text-xs text-yellow-500 font-medium px-1 bg-background/80 rounded"
          style={{
            left: `${(highlightTimestamp / duration) * 100}%`,
            transform: 'translateX(-50%)',
          }}
        >
          Feedback
        </div>
      )}
    </div>
  );
}
